import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { connect } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';

import { SlideLeft } from '@prisjakt/ui/hideable/variants';
import { Floater, Positioning } from '@prisjakt/ui/floater';
import SidebarMobileContent from './components/content';

import { logoutRequest } from '../../../account/actions';
import { AppState } from '../../../../reducers';
import * as actions from './actions';
import navigationActions from '../../../navigation/actions/mobile-menu';

import { trackOpeningSidebar, trackClosingSidebar } from '../../tracking';

import * as S from './styles';

import { VIEWS } from './constants';
import { Category, View, CategoryMenuState } from '../../types';
import { User } from '../../../../types';
import { Draggable } from '../sharedTypes';
import { zindex } from 'src/ui/zindex';

type OwnProps = {
  onClose: () => void;
  menuHistory: Category[];
  onSelectHistory: (id?: string) => void;
  categories: Category[];
  isTraversingUpwards: boolean;
  onMenuChange: (state: CategoryMenuState) => void;
  open: boolean;
};

type StateProps = {
  selectedList: number;
  isAuthenticated?: boolean;
  me: User;
  currentView: View;
};

type DispatchProps = {
  onLogout: (message: string) => void;
  setMobileSidebarView: (view: View) => void;
  openListsMobile: () => void;
  openAlertsMobile: () => void;
  navigateFromMobileMenu: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

function SidebarMobile({
  categories,
  currentView,
  isAuthenticated,
  isTraversingUpwards,
  me,
  menuHistory,
  onClose,
  onLogout,
  onMenuChange,
  onSelectHistory,
  open,
  selectedList,
  setMobileSidebarView,
  navigateFromMobileMenu,
}: Props) {
  const { t } = useContext(AppContext);
  const [previousCategory, setPreviousCategory] = useState<Category | null>(
    null
  );
  const [viewportHeight, setViewportHeight] = useState<string>('100vh');
  const [draggable, setDraggable] = useState<Draggable>(false);
  const visible = open && categories && categories.length > 0;
  const currentCategory = menuHistory[menuHistory.length - 1];
  const parentCategory = menuHistory[menuHistory.length - 2];

  useEffect(() => {
    const updateViewportHeight = () => {
      if (!IS_BROWSER) return;

      // On mobile `vh` does not take browser UI into consideration,
      // causing 100vh to be higher than what is visible.
      // `window.innerHeight` is the actual visible height of the window
      // and will be updated every render.
      setViewportHeight(`${window.innerHeight}px`);
    };

    window.addEventListener('resize', updateViewportHeight);
    updateViewportHeight();

    return () => window.removeEventListener('resize', updateViewportHeight);
  }, [setViewportHeight]);

  const changeMenuLevel = useCallback(
    (category: Category) => {
      setPreviousCategory(currentCategory);
      onMenuChange({ categories: category.children, parent: category });
    },
    [currentCategory, onMenuChange, setPreviousCategory]
  );

  const openParentCategory = useCallback(() => {
    if (parentCategory) {
      setPreviousCategory(currentCategory);
      onSelectHistory(parentCategory.id);
    }
  }, [currentCategory, onSelectHistory, parentCategory]);

  const toggleDraggable = useCallback(() => {
    setDraggable(!draggable);
  }, [draggable]);

  const openView = useCallback(
    (view: View) => {
      trackOpeningSidebar(view);
      setDraggable(false);
      setMobileSidebarView(view);
    },
    [setDraggable, setMobileSidebarView]
  );

  const openRootView = useCallback(() => openView(VIEWS.ROOT), [openView]);

  const closeMenu = useCallback(() => {
    setPreviousCategory(null);
    onClose();
  }, [onClose, setPreviousCategory]);

  const closeMenuAndResetState = useCallback(() => {
    closeMenu();
    setMobileSidebarView(VIEWS.ROOT);
  }, [closeMenu, setMobileSidebarView]);

  const logoutClick = useCallback(() => {
    trackClosingSidebar(currentView, 'MobileLogoutButton');
    closeMenuAndResetState();
    onLogout(t('account', 'logout.successful'));
  }, [closeMenuAndResetState, onLogout, t, currentView]);

  const swipeOptions = useMemo(
    () => ({
      continuous: false,
      transitionEnd: (index: number) => {
        // Close menu on swipe away
        if (index > 0) {
          trackClosingSidebar(currentView, 'MobileSwipeToClose');
          closeMenuAndResetState();
        }
      },
    }),
    [closeMenuAndResetState, currentView]
  );

  return (
    <>
      <S.Overlay active={visible} />
      <S.FakeHeader visible={visible} />
      <Floater
        zIndex={zindex.menu.mobile.overlay}
        positioning={Positioning.Fixed}
      >
        <SlideLeft visible={visible} container={S.SlideLeftContainer}>
          <SidebarMobileContent
            swipeOptions={swipeOptions}
            visible={visible}
            draggable={draggable}
            viewportHeight={viewportHeight}
            closeMenuAndResetState={closeMenuAndResetState}
            currentView={currentView}
            previousCategory={previousCategory}
            currentCategory={currentCategory}
            closeMenu={closeMenu}
            openView={openView}
            isAuthenticated={isAuthenticated}
            me={me}
            openParentCategory={openParentCategory}
            changeMenuLevel={changeMenuLevel}
            isTraversingUpwards={isTraversingUpwards}
            menuHistory={menuHistory}
            toggleDraggable={toggleDraggable}
            selectedList={selectedList}
            onClose={onClose}
            openRootView={openRootView}
            logoutClick={logoutClick}
            navigateFromMobileMenu={navigateFromMobileMenu}
          />
        </SlideLeft>
      </Floater>
    </>
  );
}

const mapStateToProps = (state: AppState) => ({
  selectedList: state.areas.sidebar.selectedList,
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
  currentView: state.areas.mobileSidebar.currentView,
});

const dispatchProps = {
  onLogout: logoutRequest,
  setMobileSidebarView: actions.setMobileSidebarView,
  navigateFromMobileMenu: navigationActions.navigateFromMobileMenu,
};

export default connect(mapStateToProps, dispatchProps)(SidebarMobile);
